import { FC, useEffect, useMemo, useState } from 'react';
import {
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	Radio,
	RadioGroup,
	Table,
	TableBody,
	TableCell,
	TableRow,
	Typography,
} from '@mui/material';
import { DateTime } from 'luxon';
import { useDispatch, useSelector } from 'react-redux';
import {
	clearQuote,
	getPayableRequest,
	getQuoteRequest,
	resetPayment,
	tryToPayRequest,
} from 'redux/reducers/payments/reducer';
import {
	getPayableInvoices,
	getPayableInvoicesLoading,
	getPaymentQuote,
	getPaymentQuoteError,
	getPaymentQuoteLoading,
	getQuoteChanged,
	getTryToPayError,
	getTryToPayLoading,
	getTryToPayResult,
} from 'redux/reducers/payments/selectors';
import { getClientAccessRequest } from 'redux/reducers/paymentMethods/reducer';
import { getPaymentsBlockedMessage } from 'redux/reducers/paymentMethods/selectors';
import { TPayableInvoice, TPayableMethod } from 'services/api/payments/types';
import { newIdempotencyKey } from 'utils/apiError';
import { AmountAndCurrency } from 'ui/AmountAndCurrency';
import { WarningAlert } from 'ui/WarningAlert';
import { PaymentMethodIcon } from '../BrandIcons';
import { ClientAccessBanner } from '../ClientAccess';
import { PaymentResult } from './PaymentResult';

type TMakePaymentDialogProps = {
	open: boolean;
	onClose: () => void;
	preselectedInvoiceId?: number | null;
};

const formatDate = (value: string | null) =>
	value ? DateTime.fromISO(value).toLocaleString(DateTime.DATE_MED) : '—';

const formatDateTime = (value: string) =>
	DateTime.fromISO(value).toLocaleString(DateTime.DATETIME_SHORT);

const invoiceTitle = (invoice: TPayableInvoice) =>
	`Invoice #${invoice.invoiceNumber}${invoice.service ? ` · ${invoice.service.name}` : ''}`;

const isDirectDebit = (method: TPayableMethod | undefined) => method?.type === 'bacs_direct_debit';

const MIN_QUOTE_RENEWAL_MS = 60_000;

export const MakePaymentDialog: FC<TMakePaymentDialogProps> = ({
	open,
	onClose,
	preselectedInvoiceId,
}) => {
	const dispatch = useDispatch();
	const payable = useSelector(getPayableInvoices);
	const payableLoading = useSelector(getPayableInvoicesLoading);
	const quote = useSelector(getPaymentQuote);
	const quoteLoading = useSelector(getPaymentQuoteLoading);
	const quoteError = useSelector(getPaymentQuoteError);
	const quoteChanged = useSelector(getQuoteChanged);
	const paying = useSelector(getTryToPayLoading);
	const result = useSelector(getTryToPayResult);
	const payError = useSelector(getTryToPayError);
	// Make payment blocked by the operator: the message is shown instead of "Pay"
	const blockedMessage = useSelector(getPaymentsBlockedMessage);

	const [invoiceId, setInvoiceId] = useState<number | null>(null);
	const [methodId, setMethodId] = useState<number | null>(null);
	// one key per opened dialog and request: a repeated "Pay" of the same request keeps it
	const [idempotencyKey, setIdempotencyKey] = useState<string>('');

	useEffect(() => {
		if (!open) return;
		dispatch(resetPayment());
		dispatch(getPayableRequest());
		dispatch(getClientAccessRequest());
		setIdempotencyKey(newIdempotencyKey());
		setInvoiceId(preselectedInvoiceId ?? null);
		setMethodId(null);
	}, [open, preselectedInvoiceId, dispatch]);

	const invoices = useMemo(() => payable?.invoices ?? [], [payable]);
	const invoice = invoices.find((item) => item.invoiceId === invoiceId);
	const method = invoice?.payable
		? invoice.methods.find((item) => item.paymentMethodId === methodId)
		: undefined;
	const directDebit = isDirectDebit(method);

	// A request refused with quote_changed / quote_expired charged nothing, and the request to confirm differs
	// (new total or new quote): it gets a new idempotency key. A repeated "Pay" of the same request keeps the key.
	useEffect(() => {
		if (quoteChanged) setIdempotencyKey(newIdempotencyKey());
	}, [quoteChanged]);
	useEffect(() => {
		if (quote?.transactionId) setIdempotencyKey(newIdempotencyKey());
	}, [quote?.transactionId]);

	// the preselected invoice: pick the only method automatically
	useEffect(() => {
		if (invoice?.payable && invoice.methods.length === 1 && methodId === null) {
			setMethodId(invoice.methods[0].paymentMethodId);
		}
	}, [invoice, methodId]);

	// Direct Debit: exact GBP amount from a quote; card: no quote
	useEffect(() => {
		if (!open || !invoice || !method || result) return;
		if (isDirectDebit(method)) {
			dispatch(
				getQuoteRequest({ invoiceId: invoice.invoiceId, paymentMethodId: method.paymentMethodId }),
			);
		} else {
			dispatch(clearQuote());
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, invoice?.invoiceId, method?.paymentMethodId]);

	// an expired quote is replaced by a new one
	useEffect(() => {
		if (!open || !quote || !invoice || !method || paying || result) return undefined;
		const msLeft = DateTime.fromISO(quote.expiresAt).toMillis() - Date.now();
		if (!Number.isFinite(msLeft)) return undefined;
		// at most one automatic renewal per minute (a client clock ahead of the server must not cause a loop);
		// a quote that has expired anyway is refused by the gateway with quote_expired and renewed by the saga
		const delayMs = Math.max(msLeft + 500, MIN_QUOTE_RENEWAL_MS);
		const timer = window.setTimeout(
			() =>
				dispatch(
					getQuoteRequest({
						invoiceId: invoice.invoiceId,
						paymentMethodId: method.paymentMethodId,
					}),
				),
			delayMs,
		);
		return () => window.clearTimeout(timer);
	}, [open, quote, invoice, method, paying, result, dispatch]);

	const amounts = useMemo(() => {
		if (!invoice || !method) return null;
		if (directDebit) {
			if (!quote) return null;
			return { amount: quote.amountEur, fee: quote.fee, total: quote.totalEur, currency: 'EUR' };
		}
		if (quoteChanged) {
			return {
				amount: quoteChanged.amount,
				fee: quoteChanged.fee,
				total: quoteChanged.total,
				currency: method.currency,
			};
		}
		return {
			amount: invoice.outstandingAmount,
			fee: method.fee,
			total: method.total,
			currency: method.currency,
		};
	}, [invoice, method, directDebit, quote, quoteChanged]);

	// Another invoice or method is another request: it gets its own idempotency key (reusing the key of a request that
	// failed without an answer would be refused with 409 idempotency_conflict). A second payment of the same invoice is
	// still refused by the gateway (one active payment per invoice, the outstanding amount).
	const selectInvoice = (id: number) => {
		if (id === invoiceId) return;
		dispatch(resetPayment());
		setIdempotencyKey(newIdempotencyKey());
		setInvoiceId(id);
		setMethodId(null);
	};

	const selectMethod = (id: number) => {
		if (id === methodId) return;
		dispatch(resetPayment());
		setIdempotencyKey(newIdempotencyKey());
		setMethodId(id);
	};

	const handlePay = () => {
		if (!invoice || !method || !amounts || paying || blockedMessage) return;
		dispatch(
			tryToPayRequest({
				invoiceId: invoice.invoiceId,
				paymentMethodId: method.paymentMethodId,
				idempotencyKey,
				expectedTotalEur: amounts.total,
				quoteId: directDebit ? (quote?.transactionId ?? null) : null,
			}),
		);
	};

	const handleTryAgain = () => {
		dispatch(resetPayment());
		setIdempotencyKey(newIdempotencyKey());
		setMethodId(null);
	};

	const finished = !!result && !paying;
	// a payment already sent is still followed to its result
	const blocked = !!blockedMessage && !paying && !result;
	const canPay =
		!!invoice?.payable && !!method && !!amounts && !paying && !result && !quoteLoading && !blocked;

	// while the gateway is still processing the payment (polling): a spinner under the form
	const renderProgress = () =>
		result?.status === 'processing' && paying ? (
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1 }}>
				<CircularProgress size={22} />
				<Typography>{result.message || 'The payment is being processed…'}</Typography>
			</Box>
		) : null;

	const renderInvoices = () => {
		if (payableLoading && !payable) {
			return (
				<Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
					<CircularProgress size={24} />
				</Box>
			);
		}
		if (!invoices.length) {
			return <Typography>There are no unpaid invoices.</Typography>;
		}
		return (
			<RadioGroup
				value={invoiceId ?? ''}
				onChange={(event) => selectInvoice(Number(event.target.value))}
			>
				{invoices.map((item) => (
					<FormControlLabel
						key={item.invoiceId}
						value={item.invoiceId}
						disabled={!item.payable || paying || !!result}
						control={<Radio size="small" />}
						sx={{ alignItems: 'flex-start', mb: 1 }}
						label={
							<Box sx={{ pt: '6px' }}>
								<Typography sx={{ fontSize: '.9rem', fontWeight: 600 }}>
									{invoiceTitle(item)} ·{' '}
									<AmountAndCurrency amount={item.outstandingAmount} currency={item.currency} />
								</Typography>
								<Typography sx={{ fontSize: '.75rem', color: 'text.secondary' }}>
									Issued {formatDate(item.invoiceDate)}, due {formatDate(item.dueDate)}
								</Typography>
								{!item.payable && item.blockReason && (
									<Typography sx={{ fontSize: '.75rem', color: 'error.main' }}>
										{item.blockReason}
									</Typography>
								)}
							</Box>
						}
					/>
				))}
			</RadioGroup>
		);
	};

	const renderMethods = () => {
		if (!invoice?.payable) return null;
		if (!invoice.methods.length) {
			return (
				<WarningAlert>
					No saved payment method covers this service. Please add a payment method first.
				</WarningAlert>
			);
		}
		return (
			<RadioGroup
				value={methodId ?? ''}
				onChange={(event) => selectMethod(Number(event.target.value))}
			>
				{invoice.methods.map((item) => (
					<FormControlLabel
						key={item.paymentMethodId}
						value={item.paymentMethodId}
						disabled={paying || !!result}
						control={<Radio size="small" />}
						label={
							<Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
								<PaymentMethodIcon type={item.type} />
								<span>{item.label}</span>
							</Box>
						}
					/>
				))}
			</RadioGroup>
		);
	};

	const renderAmounts = () => {
		if (!method) return null;
		if (directDebit && quoteError && !quoteLoading && !quote) {
			return <WarningAlert>{quoteError}</WarningAlert>;
		}
		if (directDebit && quoteLoading) {
			return (
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1 }}>
					<CircularProgress size={20} />
					<Typography>Getting the exact amount in GBP…</Typography>
				</Box>
			);
		}
		if (!amounts) return null;
		return (
			<>
				<Table size="small" sx={{ mt: 1 }}>
					<TableBody>
						<TableRow>
							<TableCell sx={{ p: '6px 0', borderBottom: 0 }}>Amount</TableCell>
							<TableCell align="right" sx={{ p: '6px 0', borderBottom: 0 }}>
								<AmountAndCurrency amount={amounts.amount} currency={amounts.currency} />
							</TableCell>
						</TableRow>
						<TableRow>
							<TableCell sx={{ p: '6px 0', borderBottom: 0 }}>Fee</TableCell>
							<TableCell align="right" sx={{ p: '6px 0', borderBottom: 0 }}>
								<AmountAndCurrency amount={amounts.fee} currency={amounts.currency} />
							</TableCell>
						</TableRow>
						<TableRow>
							<TableCell sx={{ p: '6px 0', fontWeight: 'bold' }}>Total</TableCell>
							<TableCell align="right" sx={{ p: '6px 0', fontWeight: 'bold' }}>
								<AmountAndCurrency amount={amounts.total} currency={amounts.currency} bold />
							</TableCell>
						</TableRow>
						{directDebit && quote && (
							<TableRow>
								<TableCell sx={{ p: '6px 0', fontWeight: 'bold', borderBottom: 0 }}>
									Collected by Direct Debit
								</TableCell>
								<TableCell align="right" sx={{ p: '6px 0', fontWeight: 'bold', borderBottom: 0 }}>
									<AmountAndCurrency amount={quote.gbpAmount} currency="GBP" bold />
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
				{directDebit && quote && (
					<Typography sx={{ fontSize: '.8rem', color: 'text.secondary', mt: 1 }}>
						This amount is valid until {formatDateTime(quote.expiresAt)}. The payment will be
						collected by Direct Debit within about 3 working days.
					</Typography>
				)}
			</>
		);
	};

	return (
		<Dialog
			open={open}
			onClose={paying ? undefined : onClose}
			maxWidth="sm"
			fullWidth
			sx={{ '& .MuiDialog-paper': { borderRadius: '12px' } }}
		>
			<DialogTitle color="primary">Make a payment</DialogTitle>
			<DialogContent>
				{finished && result ? (
					<PaymentResult
						result={result}
						invoice={invoice}
						methodLabel={method?.label}
						directDebit={directDebit}
					/>
				) : (
					<>
						{blocked && <ClientAccessBanner messages={[blockedMessage]} sx={{ mb: 1 }} />}
						<Typography color="primary" sx={{ fontWeight: 'bold', mb: 1 }}>
							Invoice
						</Typography>
						{renderInvoices()}
						{invoice?.payable && (
							<>
								<Typography color="primary" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>
									Payment method
								</Typography>
								{renderMethods()}
							</>
						)}
						{renderAmounts()}
						{quoteChanged && !result && (
							<WarningAlert>
								The amount has changed. Please check the new amounts and confirm the payment again.
							</WarningAlert>
						)}
						{/* a 423 of "Pay" is shown by the banner above (same operator's message) */}
						{payError && !result && !blocked && (
							<WarningAlert sx={{ whiteSpace: 'pre-line' }}>{payError}</WarningAlert>
						)}
						{result && <Box sx={{ mt: 2 }}>{renderProgress()}</Box>}
					</>
				)}
			</DialogContent>
			<DialogActions>
				{finished && result?.status === 'failed' && (
					<Button onClick={handleTryAgain}>Try another method</Button>
				)}
				<Button onClick={onClose} disabled={paying} variant={finished ? 'contained' : 'text'}>
					{finished ? 'Close' : 'Cancel'}
				</Button>
				{!result && !blocked && (
					<Button variant="contained" onClick={handlePay} disabled={!canPay}>
						{paying ? <CircularProgress size={20} /> : quoteChanged ? 'Confirm and pay' : 'Pay'}
					</Button>
				)}
			</DialogActions>
		</Dialog>
	);
};
